import { useEffect, useState } from "react";
import izenuAPI from "../../../services/izenuAPI";
import { adImageUrl } from "../adImageUrl";

const EXP_OPTIONS = [
    { value: "", label: "Manter actual" },
    { value: "15d", label: "+ 15 dias" },
    { value: "1M", label: "+ 1 mês" },
    { value: "2M", label: "+ 2 meses" },
    { value: "3M", label: "+ 3 meses" },
    { value: "6M", label: "+ 6 meses" },
    { value: "1Y", label: "+ 1 ano" },
];

// Ideal size per surface — the same hints as the "Adicionar publicidade" forms.
const IMAGE_HINT = {
    banner: "Banner principal: 1200×600 px",
    home: "Banner secundário: 640×200 px",
    player: "Banner mini play: 640×100 px",
    artwork: "Capa Play: 600×600 px",
    audio: "Capa do anúncio de áudio (quadrada)",
};

export default function EditAdModal({ open, onClose, onSaved, ad, type }) {
    const [title, setTitle] = useState("");
    const [info, setInfo] = useState("");
    const [link, setLink] = useState("");
    const [webLink, setWebLink] = useState("");
    const [expKey, setExpKey] = useState("");
    const [bannerType, setBannerType] = useState("ad");
    const [saving, setSaving] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");

    // Only the carousel ("Banner slides") distinguishes advertising from Izenu's
    // own communication — the other four surfaces have no such column.
    const isBanner = type === "banner";

    useEffect(() => {
        if (open && ad) {
            setTitle(ad.title || "");
            setInfo(ad.info || ad.description || "");
            setLink(ad.link || "");
            setWebLink(ad.web_link || "");
            setExpKey("");
            setBannerType(ad.banner_type || "ad");
            setFile(null);
        }
    }, [open, ad]);

    useEffect(() => {
        if (!file) { setPreview(""); return undefined; }
        const url = URL.createObjectURL(file);
        setPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    if (!open) return null;

    // Banner slides go through the neutral-path proxy: /banner_ads/ is blocked by ad blockers.
    const current = type === "audio" ? ad.cover_url : type === "banner" ? adImageUrl(ad.uri) : ad.uri;

    const pickFile = (f) => {
        if (!f) return;
        if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) { alert("A imagem tem de ser JPG ou PNG."); return; }
        if (f.size > 3 * 1024 * 1024) { alert("A imagem tem de ter no máximo 3 MB."); return; }
        setFile(f);
    };

    const save = async () => {
        setSaving(true);
        try {
            // The image first: if it fails nothing else is changed.
            if (file) {
                const form = new FormData();
                form.append("type", type);
                form.append("id", ad.id);
                form.append("file", file);
                const { data: img } = await izenuAPI.post("/admin/ads/updateImage.php", form);
                if (!img.response) {
                    alert(img.message || "Falha ao trocar a imagem");
                    setSaving(false);
                    return;
                }
            }
            const payload = { type, id: ad.id, title, info, link };
            if (expKey) payload.exp_date = expKey;
            if (isBanner) {
                payload.banner_type = bannerType;
                payload.web_link = webLink;
            }
            const { data } = await izenuAPI.post("/admin/ads/update.php", payload);
            if (data.response) {
                if (onSaved) onSaved();
                onClose();
            } else {
                alert(data.message || "Falha ao actualizar");
            }
        } catch (e) {
            console.log(e);
            alert(e?.response?.data?.message || "Falha ao actualizar");
        }
        setSaving(false);
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 1000,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    background: "#fff", padding: 24, borderRadius: 10, width: "100%",
                    maxWidth: 500, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                }}
            >
                <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16, color: "#111827" }}>
                    Editar anúncio #{ad.id}
                </h2>

                <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
                    {type === "audio" ? "Capa" : "Imagem"}
                </label>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 4 }}>
                    {(preview || current) && (
                        <img
                            src={preview || current}
                            alt=""
                            style={{
                                width: type === "artwork" || type === "audio" ? 72 : 160, maxHeight: 90, objectFit: "contain",
                                borderRadius: 6, border: preview ? "2px solid #0ea5e9" : "1px solid #e5e7eb", background: "#f9fafb",
                            }}
                        />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <label
                            style={{
                                display: "inline-block", padding: "6px 12px", borderRadius: 6, border: "1px solid #d1d5db",
                                fontSize: 13, color: "#374151", cursor: "pointer", background: "#fff",
                            }}
                        >
                            {file ? "Escolher outra" : "Trocar imagem"}
                            <input type="file" accept="image/png,image/jpeg" style={{ display: "none" }} onChange={(e) => pickFile(e.target.files?.[0])} />
                        </label>
                        {file && (
                            <button type="button" onClick={() => setFile(null)} style={{ marginLeft: 8, fontSize: 12, color: "#6b7280", background: "none", border: "none", cursor: "pointer" }}>
                                Manter a actual
                            </button>
                        )}
                        <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                            {file ? `Nova: ${file.name}` : IMAGE_HINT[type]} · JPG ou PNG até 3 MB
                        </div>
                    </div>
                </div>
                <div style={{ height: 12 }} />

                <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>Título</label>
                <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 mb-3"
                />

                <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>Descrição</label>
                <textarea
                    value={info}
                    onChange={(e) => setInfo(e.target.value)}
                    rows={2}
                    className="border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 mb-3"
                />

                <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
                    Link <span style={{ color: "#6b7280", fontWeight: 400 }}>(deixa vazio para não ser clicável)</span>
                </label>
                <input
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder="https://... ou app://..."
                    className="border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 mb-3"
                />

                {isBanner && (
                    <>
                        <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>
                            Link exclusivo do web app <span style={{ color: "#6b7280", fontWeight: 400 }}>(opcional)</span>
                        </label>
                        <input
                            value={webLink}
                            onChange={(e) => setWebLink(e.target.value)}
                            placeholder="/studio/new ou https://..."
                            className="border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5 mb-1"
                        />
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
                            Só o web app abre este link; a app do telemóvel abre o link acima. Vazio = o web app usa o link acima.
                        </div>

                        <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>Tipo de banner</label>
                        <select
                            value={bannerType}
                            onChange={(e) => setBannerType(e.target.value)}
                            className="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg block w-full p-2.5 mb-1"
                        >
                            <option value="ad">📢 Publicidade — não aparece a assinantes</option>
                            <option value="app">📱 Padrão da app — aparece a todos</option>
                        </select>
                        <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 12 }}>
                            Quem tem assinatura só vê banners "Padrão da app". Utilizadores grátis e visitantes veem todos.
                        </div>
                    </>
                )}

                <label style={{ display: "block", fontSize: 13, color: "#374151", marginBottom: 4 }}>Estender validade</label>
                <select
                    value={expKey}
                    onChange={(e) => setExpKey(e.target.value)}
                    className="bg-gray-50 border border-gray-300 text-gray-700 text-sm rounded-lg block w-full p-2.5 mb-2"
                >
                    {EXP_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
                {ad.exp_date && (
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 16 }}>
                        Expira actualmente em: {ad.exp_date}
                    </div>
                )}

                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        style={{
                            padding: "8px 16px", borderRadius: 6, border: "1px solid #d1d5db",
                            background: "#fff", color: "#374151", fontSize: 14, cursor: "pointer",
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        style={{
                            padding: "8px 16px", borderRadius: 6, border: "none",
                            background: saving ? "#9ca3af" : "#0ea5e9", color: "#fff", fontSize: 14,
                            fontWeight: 500, cursor: saving ? "not-allowed" : "pointer",
                        }}
                    >
                        {saving ? "A guardar..." : "Guardar"}
                    </button>
                </div>
            </div>
        </div>
    );
}
